'use client'

import { Carousel } from '@heroui-pro/react/carousel'
import { AnimatePresence, motion, type PanInfo } from 'framer-motion'
import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '@/components/ui/icon'
import { MediaType } from '@/lib/types/core'
import { IMediaItem } from '@/lib/types/media'

// Embla instance type, sourced from the Pro Carousel's own `setApi` prop so we
// never import the (eslint-banned) `embla-carousel` package directly.
type EmblaApi = Parameters<NonNullable<ComponentProps<typeof Carousel>['setApi']>>[0]

interface LightboxProps {
  isOpen: boolean
  onClose: () => void
  media: IMediaItem[]
  currentIndex: number
  onIndexChange: (index: number) => void
}

type ZoomState = { active: boolean; x: number; y: number }
const ZOOM_SCALE = 2.6

/**
 * Full-screen image viewer.
 *
 * Gestures:
 * - Horizontal swipe / drag: prev / next slide (Pro Carousel — Embla).
 * - Double-tap / double-click the image: toggle a 2.6x zoom centred on the tap
 *   point. While zoomed, slide-dragging is disabled (Embla `watchDrag`).
 * - Single tap on the image: toggle chrome (controls / counter / caption).
 * - Vertical drag on the backdrop: dismiss (framer-motion).
 * - Arrow keys: prev / next; Escape: close; Space: toggle chrome.
 *
 * Chrome is shown by default; tapping the image once toggles it so the photo
 * gets the whole viewport when wanted.
 */
export function Lightbox({ isOpen, onClose, media, currentIndex, onIndexChange }: LightboxProps) {
  const [api, setApi] = useState<EmblaApi>()
  const thumbsRef = useRef<HTMLDivElement>(null)
  const [chromeVisible, setChromeVisible] = useState(true)
  const [zoom, setZoom] = useState<ZoomState>({ active: false, x: 50, y: 50 })

  // `watchDrag` reads this synchronously on pointer-down, so keep it in a ref.
  const zoomActiveRef = useRef(false)
  useEffect(() => {
    zoomActiveRef.current = zoom.active
  }, [zoom.active])

  // Last tap timestamp for the manual double-tap detector (portable to touch,
  // unlike the unreliable synthetic dblclick).
  const lastTapRef = useRef(0)
  // Pending single-tap chrome toggle, deferred past the double-tap window so a
  // second tap can cancel it before it ever flips chrome (see handleImageTap).
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Open at the clicked index without an animated jump. Recomputed only when the
  // overlay opens/closes; the Carousel is unmounted while closed, so this seeds
  // the fresh Embla instance with the current index. `watchDrag` is stable.
  // Deps are [isOpen] on purpose: currentIndex is read only at the open
  // transition (when the Carousel mounts fresh), not on every index change,
  // and watchDrag reads a ref — so a re-init mid-life is neither needed nor wanted.
  const emblaOpts = useMemo(() => ({ startIndex: currentIndex, watchDrag: () => !zoomActiveRef.current }), [isOpen])

  // Emit the active slide upward and drop any zoom when Embla changes slide.
  useEffect(() => {
    if (!api) return
    const onSelect = () => {
      onIndexChange(api.selectedScrollSnap())
      setZoom((z) => (z.active ? { ...z, active: false } : z))
    }
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
    }
  }, [api, onIndexChange])

  // Push an externally-driven index (thumbnail click) into Embla — instant jump
  // to match the previous slideTo(index, 0) behaviour.
  useEffect(() => {
    if (!api) return
    if (api.selectedScrollSnap() !== currentIndex) api.scrollTo(currentIndex, true)
  }, [api, currentIndex])

  // Keep the active thumbnail centred in the strip as the user advances.
  useEffect(() => {
    if (!isOpen) return
    const strip = thumbsRef.current
    if (!strip) return
    const btn = strip.querySelector<HTMLButtonElement>(`[data-lb-thumb='${currentIndex}']`)
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  }, [currentIndex, isOpen])

  // Reset chrome + zoom and lock body scroll while open.
  useEffect(() => {
    if (isOpen) {
      setChromeVisible(true)
      setZoom({ active: false, x: 50, y: 50 })
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prevOverflow
        if (tapTimerRef.current) {
          clearTimeout(tapTimerRef.current)
          tapTimerRef.current = null
        }
      }
    }
  }, [isOpen])

  const handlePrevious = useCallback(() => {
    api?.scrollPrev()
  }, [api])
  const handleNext = useCallback(() => {
    api?.scrollNext()
  }, [api])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        // The Carousel root's onKeyDownCapture already handled (and
        // preventDefault'd) this when it had focus — don't advance twice.
        if (!e.defaultPrevented) handlePrevious()
      } else if (e.key === 'ArrowRight') {
        if (!e.defaultPrevented) handleNext()
      } else if (e.key === ' ') {
        e.preventDefault()
        setChromeVisible((v) => !v)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, handlePrevious, handleNext])

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      // Dismiss when vertical drag is >120px or fast flick (>500 px/s).
      if (Math.abs(info.offset.y) > 120 || Math.abs(info.velocity.y) > 500) {
        onClose()
      }
    },
    [onClose]
  )

  // Single tap toggles chrome; a second tap within 300ms is a double-tap that
  // toggles zoom (centred on the tap) instead. The chrome toggle is deferred
  // past the double-tap window so tap 1 never flips chrome before tap 2 can
  // cancel it; that pre-flip-then-undo was the visible sub-300ms flicker.
  const handleImageTap = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    const now = Date.now()
    if (now - lastTapRef.current < 300) {
      // Double-tap: cancel the still-pending single-tap chrome toggle so chrome
      // never flipped, and toggle zoom centred on the tap point instead.
      lastTapRef.current = 0
      if (tapTimerRef.current) {
        clearTimeout(tapTimerRef.current)
        tapTimerRef.current = null
      }
      const rect = e.currentTarget.getBoundingClientRect()
      const x = rect.width ? ((e.clientX - rect.left) / rect.width) * 100 : 50
      const y = rect.height ? ((e.clientY - rect.top) / rect.height) * 100 : 50
      setZoom((z) => ({ active: !z.active, x, y }))
      return
    }
    lastTapRef.current = now
    // Defer the chrome toggle; a second tap within the window clears this timer.
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null
      lastTapRef.current = 0
      setChromeVisible((v) => !v)
    }, 300)
  }, [])

  if (!isOpen) return null
  const item = media[currentIndex]

  return (
    <AnimatePresence>
      <motion.div
        key='lightbox-backdrop'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className='fixed inset-0 z-[1200] bg-black/90 backdrop-blur-sm'
        // Vertical drag-to-dismiss on the backdrop; horizontal stays with the carousel.
        drag='y'
        dragElastic={0.2}
        dragConstraints={{ top: 0, bottom: 0 }}
        onDragEnd={handleDragEnd}
      >
        {/* Top chrome: close, counter */}
        <AnimatePresence>
          {chromeVisible && (
            <motion.div
              key='top-chrome'
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className='absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 sm:p-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none'
            >
              <span className='text-sm font-medium text-white/90 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm pointer-events-auto'>
                {currentIndex + 1} of {media.length}
              </span>
              <button
                type='button'
                onClick={onClose}
                aria-label='Close gallery'
                className='flex items-center justify-center w-11 h-11 text-white rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm pointer-events-auto transition-colors'
              >
                <Icon icon='close' className='w-6 h-6' />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prev / next buttons (hidden on small touch screens — swipe is primary there) */}
        <AnimatePresence>
          {chromeVisible && media.length > 1 && (
            <>
              <motion.button
                key='prev-btn'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                type='button'
                onClick={handlePrevious}
                aria-label='Previous image'
                className='hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center text-white rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm transition-colors'
              >
                <Icon icon='arrow-left' className='w-6 h-6' />
              </motion.button>
              <motion.button
                key='next-btn'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                type='button'
                onClick={handleNext}
                aria-label='Next image'
                className='hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center text-white rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm transition-colors'
              >
                <Icon icon='arrow-right' className='w-6 h-6' />
              </motion.button>
            </>
          )}
        </AnimatePresence>

        {/* Slides — height leaves room for the thumbnail strip at the bottom.
            The child-slot selectors push the fixed height down through Embla's
            viewport wrapper/viewport so each slide can centre its image. */}
        <Carousel
          opts={emblaOpts}
          setApi={setApi}
          className='w-full h-[calc(100%-128px)] [--carousel-gap:0px] [&_[data-slot=carousel-viewport-wrapper]]:h-full [&_[data-slot=carousel-viewport]]:h-full'
        >
          <Carousel.Content className='h-full'>
            {media.map((m, i) => {
              const isZoomed = zoom.active && i === currentIndex
              return (
                <Carousel.Item key={m.id || i} className='flex h-full items-center justify-center'>
                  <img
                    src={m.url}
                    alt={m.altText || `Project image ${i + 1}`}
                    draggable={false}
                    onClick={handleImageTap}
                    style={{
                      transform: isZoomed ? `scale(${ZOOM_SCALE})` : undefined,
                      transformOrigin: `${zoom.x}% ${zoom.y}%`,
                      transition: 'transform 0.2s ease',
                      cursor: isZoomed ? 'zoom-out' : 'zoom-in'
                    }}
                    className='object-contain max-w-full max-h-full select-none'
                  />
                </Carousel.Item>
              )
            })}
          </Carousel.Content>
        </Carousel>

        {/* Bottom chrome: caption sits above the thumbnail strip */}
        <AnimatePresence>
          {chromeVisible && (item?.caption || item?.altText) && (
            <motion.figcaption
              key='bottom-chrome'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className='absolute left-0 right-0 z-20 px-4 pb-2 pointer-events-none bottom-[128px] sm:px-8'
            >
              <div className='max-w-3xl mx-auto text-white pointer-events-auto'>
                {item.caption && <p className='text-sm font-medium sm:text-base'>{item.caption}</p>}
                {item.altText && item.altText !== item.caption && (
                  <p className='mt-1 text-xs text-white/70 sm:text-sm'>{item.altText}</p>
                )}
              </div>
            </motion.figcaption>
          )}
        </AnimatePresence>

        {/* Bottom chrome: thumbnail strip — scrollable, centred when it fits */}
        <AnimatePresence>
          {chromeVisible && media.length > 1 && (
            <motion.div
              key='thumb-strip'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className='absolute bottom-0 left-0 right-0 z-20 overflow-x-auto bg-gradient-to-t from-black/80 to-transparent scrollbar-hide pb-[max(env(safe-area-inset-bottom),24px)]'
            >
              <div ref={thumbsRef} className='flex gap-2 px-4 py-3 mx-auto w-max'>
                {media.map((m, i) => (
                  <button
                    key={m.id || i}
                    type='button'
                    data-lb-thumb={i}
                    onClick={() => onIndexChange(i)}
                    aria-label={`Show image ${i + 1} of ${media.length}`}
                    aria-current={i === currentIndex ? 'true' : undefined}
                    className={`relative shrink-0 w-16 h-16 overflow-hidden transition-all rounded-md border-2 ${
                      i === currentIndex
                        ? 'border-accent ring-2 ring-accent/40 opacity-100'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    {m.type === MediaType.VIDEO ? (
                      <>
                        <img
                          src={m.metadata?.thumbnailUrl || m.url}
                          alt=''
                          className='object-cover w-full h-full'
                          draggable={false}
                        />
                        <div className='absolute inset-0 flex items-center justify-center bg-black/30'>
                          <Icon icon='play' className='w-5 h-5 text-white' />
                        </div>
                      </>
                    ) : (
                      <img src={m.url} alt='' className='object-cover w-full h-full' draggable={false} />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
